import { useAccountsService } from '@hooks/useAccountsService';
import CheckIcon from '@icons/CheckIcon';
import CopyIcon from '@icons/CopyIcon';
import { isAffiliator } from '@models/accounts';
import { setupForDark } from '@theme';
import classNames from 'classnames';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

// `path` is passed in rather than read from the location: the landing pages also render inside
// the goal editor's preview modal, where the current URL is the editor route, not the public page.
export default function CopyAffiliateLinkButton({
  path,
  isGoalAuthor = false
}: {
  path: string;
  isGoalAuthor?: boolean;
}): ReactElement | null {
  const { t } = useTranslation();
  const { accountMe } = useAccountsService();
  const [copied, setCopied] = useState(false);

  const code = accountMe?.affiliate?.referralCode;
  if (!code || (!isAffiliator(accountMe?.affiliate) && !isGoalAuthor)) return null;

  const onCopy = () => {
    const url = new URL(path, window.location.origin);
    url.searchParams.set('ref', code);
    navigator.clipboard
      ?.writeText(url.toString())
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {});
  };

  return (
    <button
      type='button'
      data-testid='affiliate-copy-link-button'
      onClick={onCopy}
      aria-label={t('components.affiliateLink.copy')}
      className={classNames(
        'fixed right-md top-md z-40 flex items-center gap-xs rounded-sm border border-solid px-sm py-xs hover:opacity-80',
        setupForDark('bg-primary text-primary border-primary')
      )}
    >
      {copied ? <CheckIcon size={18} /> : <CopyIcon size={18} />}
      <span className='P5M'>
        {copied ? t('components.affiliateLink.copied') : t('components.affiliateLink.label')}
      </span>
    </button>
  );
}
